import { mergeClassNames } from '@/utils/mergeClassNames';
import { useMemo } from 'react';
import {
  TagGroup,
  TagGroupContext,
  TagGroupProps,
  TagList,
  TagListProps,
} from 'react-aria-components';

import { FilterChipProps } from './FilterChip';
import styles from './FilterChipList.module.scss';

/** @private */
export type TagGroupContextValue = {
  selectionMode?: TagGroupProps['selectionMode'];
  size?: 'compact' | 'small' | 'medium';
  variant?: 'primary' | 'neutral';
};

type Item = FilterChipProps;

export type FilterChipListProps = {
  /** A string that labels the `ChipList`. Must be used when `ChipList` is not associated with a visual label using `aria-labelledby`. */
  'aria-label'?: TagGroupProps['aria-label'];
  /** The id of the element (or elements) that labels the `ChipList`. Must be used when `ChipList` does not receive an `aria-label`. */
  'aria-labelledby'?: TagGroupProps['aria-labelledby'];
  /** The children of the `SelectionChip`. Can be a React Node (for static lists) or a function (for dynamic lists). */
  children: TagListProps<Item>['children'];
  /** The CSS `className` for the list. A function may be provided to compute the class based on state. */
  className?: TagListProps<Item>['className'];
  /** Chip objects in the collection. Must be passed when rendering a dynamic collection. */
  items?: TagListProps<Item>['items'];
  /** The initial selected keys. For use with uncontrolled selection state. */
  defaultSelectedKeys?: TagGroupProps['defaultSelectedKeys'];
  /** Handler called when a user deletes a `FilterChip`. Pass this prop to make
   * `FilterChip`s removable. */
  onRemove?: TagGroupProps['onRemove'];
  /** Handler called when the selection changes. Must be used to update controlled selection state. */
  onSelectionChange?: TagGroupProps['onSelectionChange'];
  /** Provides content to display when there are no items in the filter chip list. */
  renderEmptyState?: TagListProps<Item>['renderEmptyState'];
  /** The currently selected keys. For use with controlled selection state. */
  selectedKeys?: TagGroupProps['selectedKeys'];
  /** The visual style of the chip. */
  selectionMode?: TagGroupProps['selectionMode'];
  /** The size of the `ChipList`'s children. */
  size?: 'compact' | 'small' | 'medium';
  /** The inline style for the list. A function may be provided to compute the style based on state. */
  style?: TagListProps<Item>['style'];
  /** The visual style of the `FilterChip`. */
  variant?: 'primary' | 'neutral';
};

/**
 * `FilterChipList` combines the functionality of Filter Chip and Input Chip.
 *
 * Filter Chips are used when the user can filter out results (rows in a table or cards). When there’s not an icon already, the selected state (`isSelected`) can add a tick icon at the beginning of the Chip to clarify its selected state (which is better than just relying on color and weight for accessibility purposes).
 *
 * Filter Chips use descriptive words to filter content. They can be a good alternative to checkboxes when viewing a list or search results. Clicking or tapping on a filter chip activates it. Write filter chips with nouns that describe the category to include in the results. Avoid negative phrases like Exclude images.
 *
 * Input Chips represent discrete pieces of information entered by a person, such as team members or filter options within a search field. They enable user input and verify that input by converting text into chips. Input Chips are always dismissible.
 *
 */
export const FilterChipList = ({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  children,
  className,
  defaultSelectedKeys,
  items,
  onRemove,
  onSelectionChange,
  renderEmptyState,
  selectedKeys,
  selectionMode,
  size = 'small',
  style,
  variant = 'primary',
}: FilterChipListProps) => {
  const contextValue = useMemo(
    () => ({
      selectionMode: selectionMode ?? undefined,
      size,
      variant,
    }),
    [selectionMode, size, variant]
  );

  return (
    <TagGroupContext.Provider value={contextValue}>
      <TagGroup
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        data-zds
        defaultSelectedKeys={defaultSelectedKeys}
        onRemove={onRemove}
        onSelectionChange={onSelectionChange}
        selectedKeys={selectedKeys}
        selectionMode={selectionMode}
      >
        <TagList
          className={(renderProps) =>
            mergeClassNames([styles.root, className], renderProps)
          }
          data-zds
          items={items}
          renderEmptyState={renderEmptyState}
          style={style}
        >
          {children}
        </TagList>
      </TagGroup>
    </TagGroupContext.Provider>
  );
};
